import React, { useEffect, useState } from 'react';
import LoggedLayout from '../../layouts/LoggedLayout';
import styled from 'styled-components';
import PageHeader from 'components/PageHeader';
import Paginator from 'components/Paginator';
import ThSortable from 'components/ThSortable';
import TrSkeleton from 'components/TrSkeleton';
import EmpoweredSelector from 'components/EmpoweredSelector';
import { default as PricesService } from 'services/Prices';
import { default as StationsService } from 'services/Stations';

const Table = styled.table`
	th,
	td {
		vertical-align: middle;
		position: relative;

		&:nth-child(1) {
			text-align: center;
			width: 20px;
		}

		&:nth-child(2) {
			text-align: left;
		}

		&:nth-child(3),
		&:nth-child(4),
		&:nth-child(5),
		&:nth-child(6) {
			width: 80px;
			text-align: right;
		}
	}
`;

const Filters = styled.div`
	display: flex;
	justify-content: end;

	@media (max-width: 1200px) {
		justify-content: center;
		flex-direction: column;

		& > *,
		.empowered-selector {
			width: 100% !important;
			display: block !important;
		}

		& > * {
			margin-bottom: 10px;
		}
	}
`;

const EmpoweredSelectorWrapper = styled.div` 
	margin-right: 10px;

	.empowered-selector {
		width: 200px;
		
		.empowered-selector_label {
			height: 28px;
			white-space: nowrap;
			overflow: hidden;

			&:hover {
				background: white;
			}

			.remove-icon {
				background: white;
				border-radius: 50%;
			}
		}
	}
`;

export default function PricesApp() {
	let [prices, setPrices] = useState([]);
	let [sortDirection, setSortDirection] = useState('asc');
	let [sortField, setSortField] = useState('station_name');
	let [station, setStation] = useState(null);
	let [skeletonRows, setSkeletonRows] = useState(5);

	const sortTableClick = (field) => {
		if ( !field ) return;
		if ( field === sortField ) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
		setSortField(field);
	};	

	const loadStations = async (input, callback) => {
		let stations = await StationsService.getList({
			search: input,
			no_paginate: true
		});
		
		callback(stations?.map(el => {
			return {
				value: el,
				label: el.name
			}
		}));
	}

	useEffect(() => {

		return function cleanup() {
			PricesService.cancel();
			StationsService.cancel();
        }
	}, []);

	useEffect(() => {
		const getPrices = async () => {
			let prices = await PricesService.getAppPricesList({
				station_id: station?.id,
				sort: sortField,
				direction: sortDirection
			});

			setPrices([...prices]);
			setSkeletonRows(prices?.length);
		};
		getPrices();

		let reloadInterval = setInterval(() => {
			getPrices();
		}, 5000);

		return function cleanup() {
			clearInterval(reloadInterval);
		}
	}, [station, sortDirection, sortField]);

	return (
		<LoggedLayout>
			<div className="col-md-12 mb-3">
				<PageHeader>
					<span className="header-column header-title">Precios App</span>
					
					<span className="header-column header-icon-end">
						<i className="bi bi-droplet-half text-plenoil-gray"></i>
					</span>
				</PageHeader>
			</div>

			<div className="col-md-12 mb-3">
				<div className="bg-white p-2 rounded shadow-sm">
					<div className="row">
						<Filters className="col-lg-12 gap-1 gap-lg-2 filters-inputs">
							<EmpoweredSelectorWrapper>
								<EmpoweredSelector
									load={(input, callback) => loadStations(input, callback)}
									onChange={(value, text) => setStation(value)}
									timeout={200}
									label={station?.name}
									value={station?.id ?? null}
									placeholder="Estación"
								/>
							</EmpoweredSelectorWrapper>
						</Filters>
					</div>
				</div>
			</div>

			<div className="pt-2 col-md-12">
				<div className="px-0 col-lg pl-lg-0">
					<div className="row m-0 pb-2">
						<div className="col-12 px-0">
							<div className="h-100 p-4 background-color-blanco shadow rounded">
								<div className="table-responsive-rows">
									<div className="mvc-grid mvc-grid-header-mode">
										<div className="mvc-grid-table">
											<table>
												<thead>
													<tr>
														<ThSortable className="title_precios-almohadilla" direction={sortDirection} active={sortField === 'station_id'} onClick={() => sortTableClick('station_id')}>#</ThSortable>
														<ThSortable className="title_precios-estacion" direction={sortDirection} active={sortField === 'station_name'} onClick={() => sortTableClick('station_name')}>Estación</ThSortable>
														<ThSortable className="title_precios-goa" direction={sortDirection} active={sortField === 'goa_price'} onClick={() => sortTableClick('goa_price')}>GOA</ThSortable>
														<ThSortable className="title_precios-sp95" direction={sortDirection} active={sortField === 'sp95_price'} onClick={() => sortTableClick('sp95_price')}>SP95</ThSortable>
														<ThSortable className="title_precios-goa-aditivado" direction={sortDirection} active={sortField === 'goa_aditivado_price'} onClick={() => sortTableClick('goa_aditivado_price')}>GOA <small className="badge bg-light text-dark">ADITIVADO</small></ThSortable>
														<ThSortable className="title_precios-sp95-aditivado" direction={sortDirection} active={sortField === 'sp95_aditivado_price'} onClick={() => sortTableClick('sp95_aditivado_price')}>SP95 <small className="badge bg-light text-dark">ADITIVADO</small></ThSortable>
													</tr>
												</thead>
												<tbody>
													{ prices.map((el, idx) => {
														return (
															<tr key={idx}>
																<td className="title_precios-almohadilla">{el.station_id}</td>
																<td className="title_precios-estacion">{el.station_name}</td>
																<td className="title_precios-goa">{parseFloat(el.goa_price) !== 0 ? parseFloat(el.goa_price).toFixed(3) : '-'}</td>
																<td className="title_precios-sp95">{parseFloat(el.sp95_price) !== 0 ? parseFloat(el.sp95_price).toFixed(3) : '-'}</td>
																<td className="title_precios-goa-aditivado">{el.goa_aditivado_price && parseFloat(el.goa_aditivado_price) !== 0 ? parseFloat(el.goa_aditivado_price).toFixed(3) : '-'}</td>
																<td className="title_precios-sp95-aditivado">{el.sp95_aditivado_price && parseFloat(el.sp95_aditivado_price) !== 0 ? parseFloat(el.sp95_aditivado_price).toFixed(3) : '-'}</td>
															</tr>
														)
													}) }

													{ prices && !prices.length && <tr><td colSpan="100%">No hay datos disponibles</td></tr> }

													{ prices === undefined && <TrSkeleton rows={skeletonRows} columns={6} /> }
												</tbody>
											</table>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</LoggedLayout>
	);
}